<script setup lang="ts">

import { formatPrice } from '@/helper/Translations.ts'
import userStore from '@/store/UserStore.ts'
import FormElement from '@/components/FormElement.vue'
import ModalComponent from '@/components/ModalComponent.vue'
import { ProductResponse } from '@/services/models/ProductResponse.ts'
import ProjectController from '@/controllers/ProjectController.ts'
import { KeyNumType } from '@/helper/Types.ts'
import { psIntegration } from '@/services/PsIntegrationService.ts'

defineProps<{
  payload: {
    updateProducts: {
      phasedOut: ProductResponse[]
      priceUpdate: ProductResponse[]
      customPrices: KeyNumType
    }
    updateConfirmed: boolean
    projectId: string
  }
}>()

const emit = defineEmits(['confirmClicked', 'cancelClicked', 'closeClicked'])

function onCloseClick() {
  emit('closeClicked')
  psIntegration.closeIntegration()
}

function onCancelClick() {
  emit('cancelClicked')
  psIntegration.closeIntegration()
}

function getProductTitle(item: ProductResponse): string {
  return ProjectController.getProductTitle(item)
}
</script>

<template>
  <transition name="modal-fade">
    <ModalComponent
      :confirm-disabled="!payload.updateConfirmed"
      :has-controls="true"
      :is-closable="false"
      class="is--update"
      @cancel="onCancelClick"
      @close="onCloseClick"
      @confirm="$emit('confirmClicked')"
    >
      <template #title />
      <template #text>
        <div v-if="payload.updateProducts.phasedOut.length" class="modal__section-header">
          <div class="h2 modal__subtitle">
            {{ $t('modal.phased_out_title') }}
          </div>
          <div class="modal__update-description span">
            {{ $t('modal.phased_out_description') }}:
          </div>
        </div>
        <div class="modal__update-products-list">
          <div
            v-for="(product, i) in payload.updateProducts.phasedOut"
            :key="'update-product-' + i"
            :data-id="product.id"
            class="modal__update-product"
          >
            <div class="modal__update-image">
              <img v-if="product.pictureUrl" :alt="product.name" :src="product.pictureUrl">
            </div>
            <div class="modal__update-data">
              <div class="span">
                {{ getProductTitle(product) }}
              </div>
              <div class="span">
                {{ product.name }}
              </div>
              <div class="span">
                {{ $t('step_accessories.table_order_number') }}:
                {{ product.orderNumber }}
              </div>
            </div>
          </div>
        </div>
        <div v-if="payload.updateProducts.priceUpdate.length" class="modal__section-header">
          <div class="h2 modal__subtitle">
            {{ $t('modal.update_price_title') }}
          </div>
          <div class="modal__update-description span">
            {{ $t('modal.update_price_description') }}:
          </div>
        </div>
        <div class="modal__update-products-list">
          <div
            v-for="(product, i) in payload.updateProducts.priceUpdate"
            :key="'update-product-' + i"
            :data-id="product.id"
            class="modal__update-product"
          >
            <div class="modal__update-image">
              <img v-if="product.pictureUrl" :alt="product.name" :src="product.pictureUrl">
            </div>
            <div class="modal__update-data">
              <div class="span">
                {{ getProductTitle(product) }}
              </div>
              <div class="span">
                {{ product.name }}
              </div>
              <div class="span">
                {{ $t('step_accessories.table_order_number') }}:
                {{ product.orderNumber }}
              </div>
            </div>
            <div class="modal__update-status">
              <div class="modal__old-price span">
                {{ $t('modal.old_price') }}:
                {{ formatPrice(product.price, userStore.currentIsoLocale) }}
              </div>
              <div v-if="payload.updateProducts.customPrices[product.id] !== undefined" class="modal__old-price span">
                {{ $t('modal.custom_price') }}:
                {{ formatPrice(payload.updateProducts.customPrices[product.id], userStore.currentIsoLocale) }}
              </div>
              <div class="modal__new-price span">
                {{ $t('modal.new_price') }}:
                {{ formatPrice(product.updatedPrice, userStore.currentIsoLocale) }}
              </div>
            </div>
          </div>
        </div>

        <div class="modal__update-warning">
          <div class="modal__update-warning-icon">
            <i class="ic-alert" />
          </div>
          <div class="modal__update-warning-text">
            <div class="span">
              {{ $t('modal.update_warning1') }}<br>
              <b>{{ $t('modal.update_warning2') }}:</b>
              {{ $t('modal.update_warning3') }}
              <span v-if="payload.updateProducts.priceUpdate.length" class="span">
                {{ $t('modal.update_custom_price') }}
              </span>
            </div>
            <FormElement>
              <label class="form__checkbox">
                <input id="confirm-update-cb" v-model="payload.updateConfirmed" type="checkbox">
                <span class="form__cm" />
                {{ $t('modal.update_warning4') }}
              </label>
            </FormElement>
          </div>
        </div>
      </template>
      <template #confirm>
        {{ $t('modal.btn_update_project') }}
      </template>
      <template #cancel>
        {{ $t('planning_suite.btn_back_to_planning_suite') }}
      </template>
    </ModalComponent>
  </transition>
</template>

<style lang="scss" scoped>
.svg-sprite {
  visibility: hidden;
  position: absolute;
  width: 0;
  height: 0;
}

.modal {
  z-index: 20;

  .info {
    &__check-list {
      margin-top: rem-calc(22);
    }

    .row {
      min-height: rem-calc(300);
    }

    &__table {
      border: 1px solid $borderColorLight;
      border-radius: rem-calc(5);

      &-header,
      &-footer {
        font-weight: bold;

        span {
          &:first-child {
            text-align: left;
          }

          &:last-child {
            text-align: right;
          }
        }
      }

      div {
        display: flex;
        justify-content: space-between;
        border-bottom: 1px solid $borderColorLight;
        padding: 1rem;

        &:last-child {
          border-bottom: none;
        }
      }
    }

    &__content {
      margin-top: rem-calc(56);
    }

    &__description {
      padding: rem-calc(32);
    }

    &__intro {
      padding-right: rem-calc(32);
      padding-left: rem-calc(32);
      margin-bottom: 0;
    }

    &__title {
      display: block;

      @include themify($themes) {
        color: $textColor;
        margin-bottom: rem-calc(24);
      }
    }

    &__text {
      display: block;
      margin-top: rem-calc(14);
    }

    &__image {
      display: flex;
      align-items: center;
      justify-content: center;
      background: $bgColor;

      svg {
        height: rem-calc(215);
      }

      &.is--large {
        svg {
          width: rem-calc(338);
          height: auto;
        }
      }
    }
  }
}
</style>
